import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, LogIn, Lock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const AuthModal = ({ isOpen, onClose, selectedPlan }) => {
  const navigate = useNavigate();

  const handleLogin = () => {
    // Salvar plano selecionado para redirecionar após login
    if (selectedPlan) {
      localStorage.setItem('pendingPlanSubscription', JSON.stringify(selectedPlan));
    }
    onClose();
    navigate('/cliente/login');
  };

  const handleRegister = () => {
    // Salvar plano selecionado para redirecionar após cadastro
    if (selectedPlan) {
      localStorage.setItem('pendingPlanSubscription', JSON.stringify(selectedPlan));
    }
    onClose();
    navigate('/cliente/cadastro');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md h-fit z-50 bg-[#0a0a0a] border border-[#C6A87C]/30 rounded-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#C6A87C] to-[#B5966D] p-6">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-black/20 hover:bg-black/40 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-black" />
              </button>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-black/20 rounded-full flex items-center justify-center">
                  <Lock className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h2 className="font-oswald font-bold text-xl uppercase text-black">
                    Conta Necessária
                  </h2>
                  <p className="font-manrope text-sm text-black/70">
                    Para assinar um plano
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Plan Info */}
              {selectedPlan && (
                <div className="bg-[#121212] border border-[#C6A87C]/20 rounded-xl p-4 mb-6">
                  <p className="font-manrope text-xs text-slate-400 mb-1">Plano selecionado:</p>
                  <h3 className="font-oswald font-bold text-lg uppercase text-white mb-1">
                    {selectedPlan.name}
                  </h3>
                  <p className="font-manrope text-sm text-[#C6A87C]">
                    {selectedPlan.description} - R${selectedPlan.effectivePrice ?? selectedPlan.price}
                  </p>
                  {selectedPlan.selectedUnitName && (
                    <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/5">
                      <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                      <p className="font-manrope text-xs text-slate-400">
                        {selectedPlan.selectedUnitName}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              <div className="text-center mb-6">
                <p className="font-manrope text-slate-300 text-sm leading-relaxed">
                  Para assinar um plano mensal, você precisa ter uma conta na Brandão Barbearia.
                </p>
                <p className="font-manrope text-slate-400 text-xs mt-2">
                  É rápido e fácil!
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleRegister}
                  className="w-full bg-[#C6A87C] text-black hover:bg-[#B5966D] rounded-lg uppercase font-oswald tracking-wider font-bold py-4 h-auto"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Criar Conta
                </Button>

                <Button
                  onClick={handleLogin}
                  variant="outline"
                  className="w-full border-[#C6A87C]/50 text-[#C6A87C] hover:bg-[#C6A87C]/10 rounded-lg uppercase font-oswald tracking-wider font-bold py-4 h-auto"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Já Tenho Conta
                </Button>
              </div>

              {/* Footer note */}
              <p className="font-manrope text-xs text-slate-500 text-center mt-4">
                Após o login, você será redirecionado para o pagamento do plano.
              </p>

              {/* Link para barbeiros/admin */}
              <div className="mt-3 pt-3 border-t border-white/5 text-center">
                <p className="font-manrope text-xs text-slate-600">
                  É barbeiro ou administrador?{' '}
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate('/login'); }}
                    className="text-slate-500 hover:text-[#C6A87C] transition-colors underline underline-offset-2"
                  >
                    Entrar com usuário e senha
                  </button>
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
